import { Plus, X } from 'lucide-react';
import {
  Button,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';

import {
  type CurrentConditionIndicator,
  CURRENTCONDITIONINDICATOR_VALUES,
} from '../../../services/api/models/CurrentConditionIndicator';
import {
  type UsState,
  USSTATE_VALUES,
} from '../../../services/api/models/UsState';
import { EditSection } from './EditSection';
import type {
  CodeCodeEntry,
  DiagnosisEntry,
  ProcedureEntry,
  UB04Address,
  UB04Organization,
  UB04WorksheetData,
  WorksheetConditionContext,
} from './types';
import { MAX_UB04_OCCURRENCE_SPANS } from './types';
import { OccurrenceSpansField } from './OccurrenceSpansField';
import { WorksheetDateInput } from './WorksheetDateInput';

const MAX_OTHER_DIAGNOSES = 17;
const MAX_PATIENT_REASONS = 3;
const MAX_ECI_CODES = 3;
const MAX_PROCEDURES = 6;
const MAX_CODE_CODE_VALUES = 4;
const SPAN_POSITIONS = ['35a', '36a', '35b', '36b'] as const;
const OCCURRENCE_POSITIONS = [
  '31a',
  '32a',
  '33a',
  '34a',
  '31b',
  '32b',
  '33b',
  '34b',
  '35a',
  '36a',
  '35b',
  '36b',
] as const;

interface ShowAllUb04FieldsProps {
  value: UB04WorksheetData;
  disabled: boolean;
  onChange: (next: UB04WorksheetData) => void;
}

interface TextInputProps {
  label: string;
  value?: string | null;
  disabled: boolean;
  onChange: (value: string) => void;
}

function TextInput({ label, value, disabled, onChange }: TextInputProps) {
  return (
    <label className='space-y-1 text-xs text-muted-foreground'>
      {label}
      <Input
        inputSize='sm'
        value={value ?? ''}
        disabled={disabled}
        onChange={event => onChange(event.target.value)}
      />
    </label>
  );
}

function OrganizationFields({
  title,
  value,
  disabled,
  onChange,
}: {
  title: string;
  value?: UB04Organization;
  disabled: boolean;
  onChange: (next: UB04Organization) => void;
}) {
  const organization = value ?? {};
  const address = organization.address ?? {};
  const updateAddress = (patch: Partial<UB04Address>) =>
    onChange({ ...organization, address: { ...address, ...patch } });

  return (
    <div className='space-y-2 rounded border p-3'>
      <h4 className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
        {title}
      </h4>
      <div className='grid gap-3 md:grid-cols-3'>
        <TextInput
          label={`${title} name`}
          value={organization.name}
          disabled={disabled}
          onChange={name => onChange({ ...organization, name })}
        />
        <TextInput
          label={`${title} NPI`}
          value={organization.npi}
          disabled={disabled}
          onChange={npi => onChange({ ...organization, npi })}
        />
        <TextInput
          label={`${title} tax ID`}
          value={organization.tax_id}
          disabled={disabled}
          onChange={tax_id => onChange({ ...organization, tax_id })}
        />
        <TextInput
          label={`${title} phone`}
          value={organization.phone}
          disabled={disabled}
          onChange={phone => onChange({ ...organization, phone })}
        />
        <TextInput
          label={`${title} address line 1`}
          value={address.address1}
          disabled={disabled}
          onChange={address1 => updateAddress({ address1 })}
        />
        <TextInput
          label={`${title} address line 2`}
          value={address.address2}
          disabled={disabled}
          onChange={address2 => updateAddress({ address2 })}
        />
        <TextInput
          label={`${title} city`}
          value={address.city}
          disabled={disabled}
          onChange={city => updateAddress({ city })}
        />
        <TextInput
          label={`${title} state`}
          value={address.state}
          disabled={disabled}
          onChange={state => updateAddress({ state })}
        />
        <TextInput
          label={`${title} ZIP code`}
          value={address.postal_code}
          disabled={disabled}
          onChange={postal_code => updateAddress({ postal_code })}
        />
      </div>
    </div>
  );
}

function CodeList({
  label,
  values,
  max,
  disabled,
  onChange,
}: {
  label: string;
  values: string[];
  max: number;
  disabled: boolean;
  onChange: (next: string[]) => void;
}) {
  return (
    <div className='space-y-2'>
      {values.map((value, index) => (
        <div key={`${label}-${index}`} className='flex gap-2'>
          <TextInput
            label={`${label} ${index + 1}`}
            value={value}
            disabled={disabled}
            onChange={next =>
              onChange(
                values.map((entry, item) => (item === index ? next : entry))
              )
            }
          />
          <Button
            type='button'
            variant='ghost'
            size='icon'
            disabled={disabled}
            aria-label={`Remove ${label}`}
            onClick={() => onChange(values.filter((_, item) => item !== index))}
          >
            <X className='h-4 w-4' />
          </Button>
        </div>
      ))}
      <Button
        type='button'
        variant='outline'
        size='sm'
        disabled={disabled || values.length >= max}
        onClick={() => onChange([...values, ''])}
      >
        <Plus className='mr-1 h-3 w-3' /> Add {label}
      </Button>
    </div>
  );
}

export function ShowAllUb04Fields({
  value,
  disabled,
  onChange,
}: ShowAllUb04FieldsProps) {
  const update = (patch: Partial<UB04WorksheetData>) =>
    onChange({ ...value, ...patch });
  const conditionContext: WorksheetConditionContext =
    value.condition_context ?? {};
  const updateConditionContext = (patch: Partial<WorksheetConditionContext>) =>
    update({ condition_context: { ...conditionContext, ...patch } });
  const patient = value.patient ?? {};
  const patientAddress = patient.address ?? {};
  const spans = (value.occurrence_uses ?? []).filter(
    use => use.kind === 'span'
  );
  const occurrences = (value.occurrence_uses ?? []).filter(
    use => use.kind === 'occurrence'
  );

  return (
    <div className='space-y-6'>
      <EditSection title='Claim, provider & patient details (FL 1-17)'>
        <div className='space-y-3'>
          <OrganizationFields
            title='FL 1 Billing provider'
            value={value.billing_provider}
            disabled={disabled}
            onChange={billing_provider => update({ billing_provider })}
          />
          <OrganizationFields
            title='FL 2 Pay-to provider'
            value={value.pay_to_provider}
            disabled={disabled}
            onChange={pay_to_provider => update({ pay_to_provider })}
          />
          <div className='grid gap-3 md:grid-cols-3'>
            <TextInput
              label='FL 8 Patient last name'
              value={patient.last_name}
              disabled={disabled}
              onChange={last_name =>
                update({ patient: { ...patient, last_name } })
              }
            />
            <TextInput
              label='FL 8 Patient first name'
              value={patient.first_name}
              disabled={disabled}
              onChange={first_name =>
                update({ patient: { ...patient, first_name } })
              }
            />
            <TextInput
              label='FL 8 Patient middle name'
              value={patient.middle_name}
              disabled={disabled}
              onChange={middle_name =>
                update({ patient: { ...patient, middle_name } })
              }
            />
            <TextInput
              label='FL 8 Patient identifier'
              value={patient.patient_identifier}
              disabled={disabled}
              onChange={patient_identifier =>
                update({ patient: { ...patient, patient_identifier } })
              }
            />
            <TextInput
              label='FL 9 Address line 1'
              value={patientAddress.address1}
              disabled={disabled}
              onChange={address1 =>
                update({
                  patient: {
                    ...patient,
                    address: { ...patientAddress, address1 },
                  },
                })
              }
            />
            <TextInput
              label='FL 9 Address line 2'
              value={patientAddress.address2}
              disabled={disabled}
              onChange={address2 =>
                update({
                  patient: {
                    ...patient,
                    address: { ...patientAddress, address2 },
                  },
                })
              }
            />
            <TextInput
              label='FL 9 City'
              value={patientAddress.city}
              disabled={disabled}
              onChange={city =>
                update({
                  patient: { ...patient, address: { ...patientAddress, city } },
                })
              }
            />
            <TextInput
              label='FL 9 State'
              value={patientAddress.state}
              disabled={disabled}
              onChange={state =>
                update({
                  patient: {
                    ...patient,
                    address: { ...patientAddress, state },
                  },
                })
              }
            />
            <TextInput
              label='FL 9 ZIP code'
              value={patientAddress.postal_code}
              disabled={disabled}
              onChange={postal_code =>
                update({
                  patient: {
                    ...patient,
                    address: { ...patientAddress, postal_code },
                  },
                })
              }
            />
            <WorksheetDateInput
              label='FL 10 Date of birth'
              value={patient.date_of_birth}
              disabled={disabled}
              onChange={date_of_birth =>
                update({ patient: { ...patient, date_of_birth } })
              }
            />
            <TextInput
              label='FL 11 Sex'
              value={patient.sex}
              disabled={disabled}
              onChange={sex => update({ patient: { ...patient, sex } })}
            />
            <TextInput
              label='FL 13 Admission hour'
              value={value.admission_hour}
              disabled={disabled}
              onChange={admission_hour => update({ admission_hour })}
            />
            <TextInput
              label='FL 16 Discharge hour'
              value={value.discharge_hour}
              disabled={disabled}
              onChange={discharge_hour => update({ discharge_hour })}
            />
          </div>
        </div>
      </EditSection>

      <EditSection title='Accident, occurrence spans & responsible party (FL 29-38)'>
        <div className='space-y-3'>
          <div className='grid gap-3 rounded border p-3 md:grid-cols-3'>
            {[
              ['condition_related_to_employment', 'Related to employment'],
              [
                'condition_related_to_auto_accident',
                'Related to auto accident',
              ],
              [
                'condition_related_to_other_accident',
                'Related to other accident',
              ],
            ].map(([field, label]) => (
              <div key={field} className='flex items-center gap-2'>
                <Checkbox
                  id={`ub04-${field}`}
                  checkboxSize='sm'
                  checked={Boolean(
                    conditionContext[field as keyof WorksheetConditionContext]
                  )}
                  disabled={disabled}
                  onCheckedChange={checked =>
                    updateConditionContext({ [field]: Boolean(checked) })
                  }
                />
                <label htmlFor={`ub04-${field}`} className='text-sm'>
                  {label}
                </label>
              </div>
            ))}
            <div className='space-y-1 text-xs text-muted-foreground'>
              <label htmlFor='ub04-accident-state'>FL 29 Accident state</label>
              <div className='flex gap-2'>
                <Select
                  value={value.accident_state ?? ''}
                  disabled={disabled}
                  onValueChange={accident_state =>
                    update({ accident_state: accident_state as UsState })
                  }
                >
                  <SelectTrigger selectSize='sm' id='ub04-accident-state'>
                    <SelectValue placeholder='Select state' />
                  </SelectTrigger>
                  <SelectContent>
                    {USSTATE_VALUES.map(state => (
                      <SelectItem key={state} value={state}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  disabled={disabled || !value.accident_state}
                  onClick={() => update({ accident_state: null })}
                >
                  Clear
                </Button>
              </div>
            </div>
            <WorksheetDateInput
              label='Accident date'
              value={conditionContext.accident_date}
              disabled={disabled}
              onChange={accident_date =>
                updateConditionContext({ accident_date })
              }
            />
            <div className='space-y-1 text-xs text-muted-foreground'>
              <label htmlFor='ub04-current-condition-indicator'>
                Current condition indicator
              </label>
              <div className='flex gap-2'>
                <Select
                  value={conditionContext.current_condition_indicator ?? ''}
                  disabled={disabled}
                  onValueChange={current_condition_indicator =>
                    updateConditionContext({
                      current_condition_indicator:
                        current_condition_indicator as CurrentConditionIndicator,
                    })
                  }
                >
                  <SelectTrigger
                    selectSize='sm'
                    id='ub04-current-condition-indicator'
                  >
                    <SelectValue placeholder='Select indicator' />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENTCONDITIONINDICATOR_VALUES.map(indicator => (
                      <SelectItem key={indicator} value={indicator}>
                        {indicator}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  disabled={
                    disabled || !conditionContext.current_condition_indicator
                  }
                  onClick={() =>
                    updateConditionContext({
                      current_condition_indicator: null,
                    })
                  }
                >
                  Clear
                </Button>
              </div>
            </div>
            <WorksheetDateInput
              label='Current illness/injury date'
              value={conditionContext.date_of_current_illness_or_injury}
              disabled={disabled}
              onChange={date_of_current_illness_or_injury =>
                updateConditionContext({ date_of_current_illness_or_injury })
              }
            />
            <WorksheetDateInput
              label='Hospitalization from date'
              value={conditionContext.hospitalization_from_date}
              disabled={disabled}
              onChange={hospitalization_from_date =>
                updateConditionContext({ hospitalization_from_date })
              }
            />
            <WorksheetDateInput
              label='Hospitalization through date'
              value={conditionContext.hospitalization_to_date}
              disabled={disabled}
              onChange={hospitalization_to_date =>
                updateConditionContext({ hospitalization_to_date })
              }
            />
            <TextInput
              label='Outside lab charge'
              value={conditionContext.outside_lab_charge}
              disabled={disabled}
              onChange={outside_lab_charge =>
                updateConditionContext({ outside_lab_charge })
              }
            />
          </div>
          <div>
            <h4 className='mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
              FL 35-36 Occurrence spans
            </h4>
            <OccurrenceSpansField
              rows={spans.map(span => ({
                code: span.code,
                from_date: span.from_date ?? '',
                through_date: span.through_date ?? '',
              }))}
              maxRows={Math.max(
                0,
                Math.min(
                  MAX_UB04_OCCURRENCE_SPANS,
                  OCCURRENCE_POSITIONS.length - occurrences.length
                )
              )}
              onChange={rows => {
                const nextSpans = rows
                  .slice(0, SPAN_POSITIONS.length)
                  .map((row, index) => ({
                    position: SPAN_POSITIONS[index],
                    kind: 'span' as const,
                    code: row.code,
                    from_date: row.from_date || null,
                    through_date: row.through_date || null,
                  }));
                const spanPositions = new Set<string>(
                  nextSpans.map(span => span.position)
                );
                const occurrencePositions = OCCURRENCE_POSITIONS.filter(
                  position => !spanPositions.has(position)
                );
                update({
                  occurrence_uses: [
                    ...occurrences.map((occurrence, index) => ({
                      ...occurrence,
                      position:
                        occurrencePositions[index] ?? occurrence.position,
                    })),
                    ...nextSpans,
                  ],
                });
              }}
              capNote={<span>Up to two occurrence spans (FL 35-36).</span>}
            />
          </div>
          <OrganizationFields
            title='FL 38 Responsible party'
            value={value.responsible_party}
            disabled={disabled}
            onChange={responsible_party => update({ responsible_party })}
          />
        </div>
      </EditSection>

      <EditSection title='Diagnoses, procedures & other codes (FL 66-74, 81)'>
        <div className='space-y-4'>
          <div className='grid gap-3 rounded border p-3 md:grid-cols-3'>
            <TextInput
              label='FL 66 Diagnosis qualifier'
              value={value.diagnosis_qualifier}
              disabled={disabled}
              onChange={diagnosis_qualifier => update({ diagnosis_qualifier })}
            />
            <TextInput
              label='FL 67 Principal diagnosis'
              value={value.principal_diagnosis?.code}
              disabled={disabled}
              onChange={code =>
                update({
                  principal_diagnosis: code
                    ? { ...value.principal_diagnosis, code }
                    : null,
                })
              }
            />
            <TextInput
              label='FL 67 Principal qualifier'
              value={value.principal_diagnosis?.qualifier}
              disabled={disabled}
              onChange={qualifier =>
                update({
                  principal_diagnosis: value.principal_diagnosis
                    ? { ...value.principal_diagnosis, qualifier }
                    : null,
                })
              }
            />
            <TextInput
              label='FL 67 Principal POA'
              value={value.principal_diagnosis?.present_on_admission}
              disabled={disabled}
              onChange={present_on_admission =>
                update({
                  principal_diagnosis: value.principal_diagnosis
                    ? { ...value.principal_diagnosis, present_on_admission }
                    : null,
                })
              }
            />
            <TextInput
              label='FL 69 Admitting diagnosis'
              value={value.admitting_diagnosis}
              disabled={disabled}
              onChange={admitting_diagnosis => update({ admitting_diagnosis })}
            />
            <TextInput
              label='FL 71 PPS code'
              value={value.pps_code}
              disabled={disabled}
              onChange={pps_code => update({ pps_code })}
            />
            <TextInput
              label='FL 63 Physician referral number'
              value={value.physician_referral_number}
              disabled={disabled}
              onChange={physician_referral_number =>
                update({ physician_referral_number })
              }
            />
          </div>
          <CodeList
            label='FL 70 Patient reason diagnosis'
            values={value.patient_reason_diagnoses ?? []}
            max={MAX_PATIENT_REASONS}
            disabled={disabled}
            onChange={patient_reason_diagnoses =>
              update({ patient_reason_diagnoses })
            }
          />
          <DiagnosisList
            label='FL 67 Other diagnosis'
            values={value.other_diagnoses ?? []}
            max={MAX_OTHER_DIAGNOSES}
            disabled={disabled}
            onChange={other_diagnoses => update({ other_diagnoses })}
          />
          <DiagnosisList
            label='FL 72 ECI code'
            values={value.eci_codes ?? []}
            max={MAX_ECI_CODES}
            disabled={disabled}
            onChange={eci_codes => update({ eci_codes })}
          />
          <ProcedureList
            values={value.procedures ?? []}
            disabled={disabled}
            onChange={procedures => update({ procedures })}
          />
          <CodeCodeList
            values={value.code_code_values ?? []}
            disabled={disabled}
            onChange={code_code_values => update({ code_code_values })}
          />
        </div>
      </EditSection>
    </div>
  );
}

function DiagnosisList({
  label,
  values,
  max,
  disabled,
  onChange,
}: {
  label: string;
  values: DiagnosisEntry[];
  max: number;
  disabled: boolean;
  onChange: (next: DiagnosisEntry[]) => void;
}) {
  const update = (index: number, patch: Partial<DiagnosisEntry>) =>
    onChange(
      values.map((value, item) =>
        item === index ? { ...value, ...patch } : value
      )
    );
  return (
    <div className='space-y-2'>
      {values.map((entry, index) => (
        <div key={`${label}-${index}`} className='grid gap-2 md:grid-cols-4'>
          <TextInput
            label={`${label} ${index + 1}`}
            value={entry.code}
            disabled={disabled}
            onChange={code => update(index, { code })}
          />
          <TextInput
            label='Qualifier'
            value={entry.qualifier}
            disabled={disabled}
            onChange={qualifier => update(index, { qualifier })}
          />
          <TextInput
            label='POA'
            value={entry.present_on_admission}
            disabled={disabled}
            onChange={present_on_admission =>
              update(index, { present_on_admission })
            }
          />
          <Button
            type='button'
            variant='ghost'
            size='icon'
            disabled={disabled}
            aria-label={`Remove ${label}`}
            onClick={() => onChange(values.filter((_, item) => item !== index))}
          >
            <X className='h-4 w-4' />
          </Button>
        </div>
      ))}
      <Button
        type='button'
        variant='outline'
        size='sm'
        disabled={disabled || values.length >= max}
        onClick={() => onChange([...values, { code: '' }])}
      >
        <Plus className='mr-1 h-3 w-3' /> Add {label}
      </Button>
    </div>
  );
}

function ProcedureList({
  values,
  disabled,
  onChange,
}: {
  values: ProcedureEntry[];
  disabled: boolean;
  onChange: (next: ProcedureEntry[]) => void;
}) {
  const update = (index: number, patch: Partial<ProcedureEntry>) =>
    onChange(
      values.map((value, item) =>
        item === index ? { ...value, ...patch } : value
      )
    );
  return (
    <div className='space-y-2'>
      <h4 className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
        FL 74 Procedures
      </h4>
      {values.map((entry, index) => (
        <div key={`procedure-${index}`} className='grid gap-2 md:grid-cols-3'>
          <TextInput
            label={`FL 74${index === 0 ? '' : String.fromCharCode(96 + index)} Procedure code`}
            value={entry.code}
            disabled={disabled}
            onChange={code => update(index, { code })}
          />
          <WorksheetDateInput
            label='Procedure date'
            value={entry.date}
            disabled={disabled}
            onChange={date => update(index, { date })}
          />
          <Button
            type='button'
            variant='ghost'
            size='icon'
            disabled={disabled}
            aria-label='Remove procedure'
            onClick={() => onChange(values.filter((_, item) => item !== index))}
          >
            <X className='h-4 w-4' />
          </Button>
        </div>
      ))}
      <Button
        type='button'
        variant='outline'
        size='sm'
        disabled={disabled || values.length >= MAX_PROCEDURES}
        onClick={() => onChange([...values, { code: '' }])}
      >
        <Plus className='mr-1 h-3 w-3' /> Add procedure
      </Button>
    </div>
  );
}

function CodeCodeList({
  values,
  disabled,
  onChange,
}: {
  values: CodeCodeEntry[];
  disabled: boolean;
  onChange: (next: CodeCodeEntry[]) => void;
}) {
  const update = (index: number, patch: Partial<CodeCodeEntry>) =>
    onChange(
      values.map((value, item) =>
        item === index ? { ...value, ...patch } : value
      )
    );
  return (
    <div className='space-y-2'>
      <h4 className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
        FL 81 Code-code values
      </h4>
      {values.map((entry, index) => (
        <div key={`code-code-${index}`} className='grid gap-2 md:grid-cols-3'>
          <TextInput
            label='FL 81 Qualifier'
            value={entry.qualifier}
            disabled={disabled}
            onChange={qualifier => update(index, { qualifier })}
          />
          <TextInput
            label='FL 81 Code'
            value={entry.code}
            disabled={disabled}
            onChange={code => update(index, { code })}
          />
          <Button
            type='button'
            variant='ghost'
            size='icon'
            disabled={disabled}
            aria-label='Remove code-code value'
            onClick={() => onChange(values.filter((_, item) => item !== index))}
          >
            <X className='h-4 w-4' />
          </Button>
        </div>
      ))}
      <Button
        type='button'
        variant='outline'
        size='sm'
        disabled={disabled || values.length >= MAX_CODE_CODE_VALUES}
        onClick={() => onChange([...values, {}])}
      >
        <Plus className='mr-1 h-3 w-3' /> Add code-code value
      </Button>
    </div>
  );
}
